import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useActivityTracker } from "./hooks/useActivityTracker";
import { useAutoLogout } from "./hooks/useAutoLogout";
import { useDisableAutocomplete } from "./hooks/useDisableAutocomplete";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import RoleRoute from "./components/RoleRoute";
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const ConsultantsPage = React.lazy(() => import("./pages/ConsultantsPage"));
const ClientsPage = React.lazy(() => import("./pages/ClientsPage"));
const VendorsPage = React.lazy(() => import("./pages/VendorsPage"));
const PaymentsOverviewPage = React.lazy(() => import("./pages/PaymentsOverviewPage"));
const RequestPaymentsPage = React.lazy(() => import("./pages/RequestPaymentsPage"));
const InvoicesPage = React.lazy(() => import("./pages/InvoicesPage"));
const DraftInvoiceEditorPage = React.lazy(() => import("./pages/DraftInvoiceEditorPage"));
const PaymentSlipViewPage = React.lazy(() => import("./pages/PaymentSlipViewPage"));
const PaymentNoticeViewPage = React.lazy(() => import("./pages/PaymentNoticeViewPage"));
const ExpensesPage = React.lazy(() => import("./pages/ExpensesPage"));
const TimesheetBatchDetailPage = React.lazy(() => import("./pages/TimesheetBatchDetailPage"));
const VendorPortalPage = React.lazy(() => import("./pages/VendorPortalPage"));
const SignContractPage = React.lazy(() => import("./pages/SignContractPage"));
const FinancePage = React.lazy(() => import("./pages/FinancePage"));
const ProfilePage = React.lazy(() => import("./pages/ProfilePage"));
const SettingsPage = React.lazy(() => import("./pages/SettingsPage"));
const PreferencesPage = React.lazy(() => import("./pages/PreferencesPage"));
const OptionsPage = React.lazy(() => import("./pages/OptionsPage"));
const UserGuidePage = React.lazy(() => import("./pages/UserGuidePage"));
const MissionsPage = React.lazy(() => import("./pages/MissionsPage"));
const ContractsPage = React.lazy(() => import("./pages/ContractsPage"));
const ContractEditorPage = React.lazy(() => import("./pages/ContractEditorPage"));
const KpiHistoryPage = React.lazy(() => import("./pages/KpiHistoryPage"));

function App() {
  useActivityTracker();
  useAutoLogout();
  useDisableAutocomplete();

  return (
    <React.Suspense fallback={<div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-adminlte-primary" /></div>}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/vendor-portal/:token" element={<VendorPortalPage />} />
      <Route path="/sign-contract/:token" element={<SignContractPage />} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/consultants" element={<ConsultantsPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/vendors" element={<VendorsPage />} />
        <Route path="/missions" element={<MissionsPage />} />
        <Route path="/contracts" element={<ContractsPage />} />
        <Route path="/contracts/:id" element={<ContractEditorPage />} />
        <Route path="/payments" element={<PaymentsOverviewPage />} />
        <Route path="/payments/slip/:id" element={<PaymentSlipViewPage />} />
        <Route path="/payments/notice/:id" element={<PaymentNoticeViewPage />} />
        <Route path="/request-payments" element={<RequestPaymentsPage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/invoices/draft/:id" element={<DraftInvoiceEditorPage />} />
        <Route
          path="/finance"
          element={
            <RoleRoute allowedRoles={["admin", "superuser"]}>
              <FinancePage />
            </RoleRoute>
          }
        />
        <Route path="/accounts" element={<Navigate to="/finance" replace />} />
        <Route path="/su-settings" element={<Navigate to="/options?tab=su-settings" replace />} />
        <Route path="/templates" element={<Navigate to="/settings?tab=templates" replace />} />
        <Route path="/documents" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/expenses"
          element={
            <RoleRoute allowedRoles={["admin", "superuser"]}>
              <ExpensesPage />
            </RoleRoute>
          }
        />
        <Route path="/timesheets" element={<TimesheetBatchDetailPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/preferences" element={<PreferencesPage />} />
        <Route
          path="/settings"
          element={
            <RoleRoute allowedRoles={["admin", "superuser"]}>
              <SettingsPage />
            </RoleRoute>
          }
        />
        <Route path="/users" element={<Navigate to="/options?tab=users" replace />} />
        <Route
          path="/options"
          element={
            <RoleRoute allowedRoles={["superuser"]}>
              <OptionsPage />
            </RoleRoute>
          }
        />
        <Route path="/logs" element={<Navigate to="/options?tab=logs" replace />} />
        <Route path="/user-guide" element={<UserGuidePage />} />
        <Route path="/dashboard/history/:kpiType" element={<KpiHistoryPage />} />
      </Route>
    </Routes>
    </React.Suspense>
  );
}

export default App;
